import { flag } from 'country-emoji';
import { capitalize } from 'lodash';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import Icon from '../Icon/Icon';
import { useFloating, useHover, useInteractions } from '@floating-ui/react';

export const TagComponent = ({ ref, text, icon, ...props }) => {
  return (
    <div
      ref={ref}
      {...props}
      style={{
        border: '1px solid #EBEBEB',
        fontWeight: 400,
        fontSize: 12,
        letterSpacing: 0.5,
        color: '#5A5A5A',
        paddingRight: 12,
        paddingLeft: 8,
        borderRadius: 500,
        whiteSpace: 'nowrap',

        display: 'flex',
        gap: 6,
        alignItems: 'center',
      }}
    >
      {icon}
      {text}
    </div>
  );
};

export const flags = {
  english: flag('us'),
  french: flag('fr'),
  spanish: flag('es'),
  portuguese: flag('pt'),
  chinese: flag('cn'),
  japanese: flag('jp'),
  tagalog: flag('tl'),
  vietnamese: flag('vi'),
  asl: `🫱`,
};

export const LanguaguesTag = ({ languagues }) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (languagues?.length || 0) > 1 ? setIsOpen : undefined,
  });

  const hover = useHover(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  const { formatMessage } = useIntl();
  const text = useMemo(() => {
    if ((languagues?.length || 0) === 0) return null;
    if ((languagues?.length || 0) === 1)
      return capitalize(languagues?.[0]) + formatMessage({ id: 'Cappa.tag.languagesOnly' });
    return languagues?.length + formatMessage({ id: 'Cappa.tag.languages' });
  }, [languagues]);

  if (text === null) return null;

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()}>
        <TagComponent text={text} icon={<Icon icon="LangSmall" />} />
      </div>
      {isOpen && (
        <div
          ref={refs.setFloating}
          {...getFloatingProps()}
          style={{
            border: '1px solid #D3D3D3',
            borderRadius: 4,
            position: 'absolute',
            pointerEvents: 'none',
            boxShadow: '0px 5px 20px 0px #0000001A',
            backgroundColor: 'white',
            padding: '8px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            ...floatingStyles,
          }}
        >
          {languagues.map(languague => (
            <div
              key={languague}
              style={{
                fontSize: 12,
                lineHeight: '16px',
                fontWeight: 400,
                color: '#5A5A5A',
                display: 'flex',
                gap: 6,
                alignItems: 'center',
              }}
            >
              {flags[languague] || '??'} {capitalize(languague)}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export const CertificatesTag = ({ certificates }) => {
  const { formatMessage } = useIntl();
  return (
    <TagComponent
      text={
        certificates +
        (certificates > 1
          ? formatMessage({
              id: 'Cappa.tag.certificates',
              defaultMessage: ' certifications',
            })
          : formatMessage({
              id: 'Cappa.tag.certification',
              defaultMessage: ' certification',
            }))
      }
      icon={<Icon icon="RewardSmall" />}
    />
  );
};
export const LocationTag = ({ text }) => {
  const textLimited = (text || '').length > 18 ? (text || '').slice(0, 18) + '...' : text || '';
  return <TagComponent text={textLimited} icon={<Icon icon={'MapMarkSmall'} />} />;
};

export const YearsOfExperienceTag = ({ years }) => {
  const { formatMessage } = useIntl();
  return (
    <TagComponent
      text={
        years +
        formatMessage({
          id: 'Cappa.tag.experience',
        })
      }
      icon={<Icon icon={'StarSmall'} />}
    />
  );
};

export const ReviewsTag = ({ reviews }) => {
  const { formatMessage } = useIntl();
  return (
    <TagComponent
      text={reviews + formatMessage({ id: 'Cappa.tag.reviews' })}
      icon={<Icon icon={'StarSmall'} />}
    />
  );
};
